import { useQuery } from "@tanstack/react-query";
import { useCentraClient } from "@trydig/centra/client";
import type { GeoLocale } from "@trydig/next/i18n";
import { useAtom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";
import { countries, useGeo } from "@/i18n/routing";

const storage = createJSONStorage<boolean>(() => window.sessionStorage);
const openAtom = atomWithStorage("geo-popup-open", false, storage);

export const useGeoPopup = () => {
	const { country, action, language, pending } = useGeo();
	const client = useCentraClient();

	const [requestedClose, setRequestedClose] = useAtom(openAtom);

	const { data: browserCountry, error } = useQuery({
		queryKey: ["geo-matcher"],
		queryFn: async () => {
			const { data, error } = await client.GET("/selection", {
				headers: {
					"API-Token": "",
				},
			});

			if (error) {
				throw error;
			}

			return data.location?.country;
		},
	});

	const isLoading = !browserCountry && !error;

	const isMismatch = country !== browserCountry;

	const shouldOpen = isMismatch && !isLoading && !requestedClose && !error;

	const close = (shouldChangeLanguage = false) => {
		setRequestedClose(true);

		if (shouldChangeLanguage && browserCountryInfo?.language) {
			const targetLanguage = browserCountryInfo.language.toLowerCase();
			action(`${targetLanguage}-${country.toUpperCase()}` as GeoLocale);
		}
	};

	const changeCountry = (shouldChangeLanguage: boolean) => {
		setRequestedClose(true);
		const targetLanguage =
			shouldChangeLanguage && browserCountryInfo?.language
				? browserCountryInfo.language.toLowerCase()
				: language.toLowerCase();

		action(`${targetLanguage}-${browserCountry?.toUpperCase()}` as GeoLocale);
	};

	const browserCountryInfo = countries.find(
		(c) => c.country === browserCountry,
	);
	const currentCountryInfo = countries.find((c) => c.country === country);

	return {
		isMismatch,
		isLoading,
		shouldOpen,
		close,
		browserCountryInfo,
		currentCountryInfo,
		changeCountry,
		pending,
	};
};
